import { AiActionBarButton } from '@vimeo/ai-action-menu';
import {
  Button,
  Flex,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuGroup,
  MenuItem,
  MenuList,
  Tooltip,
  useBreakpointValue,
} from '@vimeo/bokeh';
import {
  BookmarkChapter,
  Send,
  FileSearchAlt,
  Heart,
  HeartFilled,
  Comment,
  Clock,
  ClockFilled,
  DownloadImport,
  Flag,
  EllipsisV,
} from '@vimeo/bokeh-icons';
import { translate } from '@vimeo/i18n-runtime';

import { useResizeObserver } from '../../hooks/use-resize-observer';
import { AnimatedLikeButton, ListOverflowItems, RowRenderIfSpace } from './components';
import { HoverAnimatedIcon } from './components/HoverAnimatedIcon';
import { SparkleIcon } from './components/SparkleIcon';
import useShouldShowChildren from './hooks/useShouldShowChildren';
import { ActionBarProps } from './types';

/**
 * Will dynamically render button items depending on space available and device type.
 * Only on desktop, x-axis scroll is disabled, any buttons that are removed from the button row will be placed in the overflow popover menu
 */
function ActionBar({
  buttonVariant = 'minimal',
  totalLikes,
  totalComments,
  totalCollections,
  likedClip = false,
  canLike = true,
  isOnWatchList,
  isWatchLaterLoading,
  isLikeLoading = false,
  disableScroll = true,
  disableTooltips = false,
  showShare = false,
  showLike = true,
  showAnimatedLike = false,
  showWatchlater = true,
  showDownload = false,
  showComments = false,
  showCommentsOnDesktop = false,
  showVimeoAI = false,
  showCollections = false,
  showExploreCollections = false,
  showChapters = false,
  showTranscript = false,
  showReport,
  onShareClick,
  onLikeClick,
  onCommentClick,
  onWatchLaterClick,
  onDownloadClick,
  onAskAIClick,
  onReportClick,
  onCollectionClick,
  onExploreCollectionsClick,
  onChaptersClick,
  onTranscriptClick,
}: ActionBarProps): React.ReactElement {
  // Show comments on mobile only - can't use Show due to overflow and spacing complexity
  const shouldShowComments = useBreakpointValue(
    { base: showComments, md: showCommentsOnDesktop },
    { fallback: 'md' },
  );
  const [wrapperRef, childrenShowing] = useShouldShowChildren<HTMLDivElement>();
  const shouldShowMenuButton = childrenShowing.some((show) => !show) && disableScroll;
  const {
    ref: elementRef,
    dimension: { width },
  } = useResizeObserver<HTMLDivElement>();

  const exploreCollections = translate('Explore collections');
  return (
    <Flex
      gap={100}
      borderY={!childrenShowing.length || buttonVariant !== 'minimal' ? 'none' : '1px solid'}
      borderColor="stroke"
      align="center"
      data-testid="action-bar"
      ref={elementRef}
    >
      <RowRenderIfSpace
        ref={wrapperRef}
        childrenShowing={childrenShowing}
        disableScroll={disableScroll}
      >
        {showShare ? (
          <Tooltip label={translate('Share')} placement="top">
            <IconButton
              aria-label="Share button"
              title="Share button"
              onClick={(event) => {
                onShareClick?.();
                event.currentTarget.blur();
              }}
              variant={buttonVariant}
              size="sm"
              icon={<HoverAnimatedIcon iconName="send" />}
            />
          </Tooltip>
        ) : null}

        {showLike ? (
          showAnimatedLike ? (
            <AnimatedLikeButton
              isLiked={likedClip}
              isDisabled={!canLike}
              likesCount={totalLikes}
              isLoading={isLikeLoading}
              disableTooltips={disableTooltips}
              onLikeClick={onLikeClick}
              variant={buttonVariant}
            />
          ) : (
            <Tooltip
              label={likedClip ? translate('Unlike') : translate('Like')}
              isDisabled={disableTooltips}
              placement="top"
              closeOnClick={false}
            >
              {totalLikes ? (
                <Button
                  onClick={onLikeClick}
                  variant={buttonVariant}
                  size="sm"
                  isDisabled={!canLike}
                  isLoading={isLikeLoading}
                  leftIcon={likedClip ? <HeartFilled /> : <HoverAnimatedIcon iconName="heart" />}
                >
                  {totalLikes}
                </Button>
              ) : (
                <IconButton
                  aria-label="Like button"
                  title="Like button"
                  onClick={onLikeClick}
                  variant={buttonVariant}
                  size="sm"
                  isDisabled={!canLike}
                  icon={likedClip ? <HeartFilled /> : <HoverAnimatedIcon iconName="heart" />}
                />
              )}
            </Tooltip>
          )
        ) : null}

        {shouldShowComments ? (
          <Tooltip label={translate('Comments')} placement="top" isDisabled={disableTooltips}>
            {totalComments ? (
              <Button
                onClick={onCommentClick}
                variant={buttonVariant}
                size="sm"
                leftIcon={<HoverAnimatedIcon iconName="comment" />}
              >
                {totalComments}
              </Button>
            ) : (
              <IconButton
                aria-label="Comments button"
                title="Comments button"
                onClick={onCommentClick}
                variant={buttonVariant}
                size="sm"
                icon={<HoverAnimatedIcon iconName="comment" />}
              />
            )}
          </Tooltip>
        ) : null}

        {showCollections ? (
          <Menu>
            <Tooltip
              label={translate('Add to collections')}
              placement="top"
              isDisabled={disableTooltips}
            >
              {totalCollections ? (
                <MenuButton
                  as={Button}
                  variant={buttonVariant}
                  size="sm"
                  leftIcon={<HoverAnimatedIcon iconName="3-layers" />}
                  onFocus={(e) => e.preventDefault()}
                >
                  {totalCollections}
                </MenuButton>
              ) : (
                <MenuButton
                  as={IconButton}
                  variant={buttonVariant}
                  size="sm"
                  icon={<HoverAnimatedIcon iconName="3-layers" />}
                  onFocus={(e) => e.preventDefault()}
                  title="Collections button"
                />
              )}
            </Tooltip>
            <MenuList>
              <MenuGroup title={translate('Add to')}>
                <MenuItem onClick={() => onCollectionClick?.('showcases')}>
                  {translate('Showcase')}
                </MenuItem>
                <MenuItem onClick={() => onCollectionClick?.('channels')}>
                  {translate('Channel')}
                </MenuItem>
                <MenuItem onClick={() => onCollectionClick?.('groups')}>
                  {translate('Group')}
                </MenuItem>
              </MenuGroup>
              <MenuDivider />
              {showExploreCollections && (
                <MenuItem onClick={onExploreCollectionsClick}>{exploreCollections}</MenuItem>
              )}
            </MenuList>
          </Menu>
        ) : null}

        {showWatchlater ? (
          <Tooltip
            label={
              isOnWatchList ? translate('Remove from Watch later') : translate('Add to Watch later')
            }
            isDisabled={disableTooltips}
            placement="top"
            closeOnClick={false}
          >
            <IconButton
              aria-label="Watch later button"
              onClick={onWatchLaterClick}
              isLoading={isWatchLaterLoading}
              variant={buttonVariant}
              size="sm"
              icon={isOnWatchList ? <ClockFilled /> : <HoverAnimatedIcon iconName="clock" />}
            >
              {translate('Watch later')}
            </IconButton>
          </Tooltip>
        ) : null}

        {showDownload ? (
          <Tooltip label={translate('Download')} placement="top" isDisabled={disableTooltips}>
            <IconButton
              aria-label="Download button"
              onClick={onDownloadClick}
              variant={buttonVariant}
              size="sm"
              icon={<HoverAnimatedIcon iconName="download-import" />}
            >
              {translate('Download')}
            </IconButton>
          </Tooltip>
        ) : null}

        {showReport ? (
          <Tooltip label={translate('Report')} placement="top" isDisabled={disableTooltips}>
            <IconButton
              aria-label="Report button"
              onClick={onReportClick}
              variant={buttonVariant}
              size="sm"
              icon={<HoverAnimatedIcon iconName="flag" />}
            >
              {translate('Report')}
            </IconButton>
          </Tooltip>
        ) : null}
      </RowRenderIfSpace>

      {shouldShowMenuButton ? (
        <Menu>
          <MenuButton as={IconButton} variant={buttonVariant} size="xs" icon={<EllipsisV />} />
          <ListOverflowItems childrenShowing={childrenShowing}>
            {showShare ? (
              <MenuItem
                onClick={() => {
                  onShareClick?.();
                }}
                icon={<Send />}
              >
                {translate('Share')}
              </MenuItem>
            ) : null}

            {showLike ? (
              <MenuItem
                onClick={() => {
                  onLikeClick?.();
                }}
                icon={likedClip ? <HeartFilled /> : <Heart />}
              >
                {translate('Like')}
              </MenuItem>
            ) : null}

            {shouldShowComments ? (
              <MenuItem
                onClick={() => {
                  onCommentClick?.();
                }}
                icon={<Comment />}
              >
                {translate('Comments')}
              </MenuItem>
            ) : null}

            {showCollections ? (
              <MenuGroup title={translate('Add to')}>
                <MenuItem onClick={() => onCollectionClick?.('showcases')}>
                  {translate('Showcase')}
                </MenuItem>
                <MenuItem onClick={() => onCollectionClick?.('channels')}>
                  {translate('Channel')}
                </MenuItem>
                <MenuItem onClick={() => onCollectionClick?.('groups')}>
                  {translate('Group')}
                </MenuItem>
                <MenuDivider />
                {showExploreCollections && (
                  <MenuItem onClick={onExploreCollectionsClick}>{exploreCollections}</MenuItem>
                )}
                <MenuDivider />
              </MenuGroup>
            ) : null}

            {showWatchlater ? (
              <MenuItem
                onClick={() => {
                  onWatchLaterClick?.();
                }}
                icon={isOnWatchList ? <ClockFilled /> : <Clock />}
              >
                {translate('Watch later')}
              </MenuItem>
            ) : null}

            {showDownload ? (
              <MenuItem
                onClick={() => {
                  onDownloadClick?.();
                }}
                icon={<DownloadImport />}
              >
                {translate('Download')}
              </MenuItem>
            ) : null}
            {showReport ? (
              <MenuItem
                onClick={() => {
                  onReportClick?.();
                }}
                icon={<Flag />}
              >
                {translate('Report')}
              </MenuItem>
            ) : null}
          </ListOverflowItems>
        </Menu>
      ) : null}

      {/* Below container width of X change buttons to be icon buttons */}
      <HStack ml="auto">
        {width < 480 ? (
          <>
            {showChapters ? (
              <Tooltip label={translate('Chapters')} placement="top" isDisabled={disableTooltips}>
                <IconButton
                  aria-label="Chapters button"
                  icon={<BookmarkChapter />}
                  variant={buttonVariant}
                  size="sm"
                  onClick={onChaptersClick}
                />
              </Tooltip>
            ) : null}
            {showTranscript ? (
              <Tooltip label={translate('Transcript')} placement="top" isDisabled={disableTooltips}>
                <IconButton
                  aria-label="Transcript button"
                  icon={<FileSearchAlt />}
                  variant={buttonVariant}
                  size="sm"
                  onClick={onTranscriptClick}
                />
              </Tooltip>
            ) : null}
            {showVimeoAI ? (
              <Tooltip label={translate('Vimeo AI')} placement="top" isDisabled={disableTooltips}>
                <IconButton
                  aria-label="Vimeo AI button"
                  icon={<SparkleIcon />}
                  sx={{
                    '*': {
                      fill: 'text-primary', // This is a temporary fix for the icon color as SparkleIcon is not a part of the Bokeh icon set
                    },
                  }}
                  variant={buttonVariant}
                  size="sm"
                  onClick={onAskAIClick}
                />
              </Tooltip>
            ) : null}
          </>
        ) : (
          <>
            {showChapters ? (
              <Button
                leftIcon={<BookmarkChapter />}
                variant={buttonVariant}
                size="sm"
                onClick={onChaptersClick}
              >
                {translate('Chapters')}
              </Button>
            ) : null}
            {showTranscript ? (
              <Button
                leftIcon={<FileSearchAlt />}
                variant={buttonVariant}
                size="sm"
                onClick={onTranscriptClick}
              >
                {translate('Transcript')}
              </Button>
            ) : null}
            {showVimeoAI ? (
              <AiActionBarButton onClick={onAskAIClick} variant={buttonVariant} />
            ) : null}
          </>
        )}
      </HStack>
    </Flex>
  );
}

export { ActionBar };
